import { __extends, __assign } from 'tslib';
import { InvariantError, invariant } from 'ts-invariant';
import Observable from 'zen-observable';
import 'symbol-observable';
import { getOperationDefinition } from '../utilities/graphql/getFromAST.js';
import { equal } from '@wry/equality';
import { tryFunctionOrLogError } from '../utilities/common/errorHandling.js';
import { isNonEmptyArray } from '../utilities/common/arrays.js';
import { ApolloError } from '../errors/ApolloError.js';
import { cloneDeep } from '../utilities/common/cloneDeep.js';
import { NetworkStatus, isNetworkRequestInFlight } from './networkStatus.js';
import { FetchType } from './types.js';

var hasError = function hasError(storeValue, policy) {
  if (policy === void 0) {
    policy = 'none';
  }

  return storeValue && (storeValue.networkError || policy === 'none' && isNonEmptyArray(storeValue.graphQLErrors));
};

var ObservableQuery = function (_super) {
  __extends(ObservableQuery, _super);

  function ObservableQuery(_a) {
    var queryManager = _a.queryManager,
        options = _a.options,
        _b = _a.shouldSubscribe,
        shouldSubscribe = _b === void 0 ? true : _b;

    var _this = _super.call(this, function (observer) {
      return _this.onSubscribe(observer);
    }) || this;

    _this.observers = new Set();
    _this.subscriptions = new Set();
    _this.observer = {
      next: function next(result) {
        if (_this.lastError || _this.isDifferentFromLastResult(result)) {
          var queryManager = _this.queryManager;
          var _a = _this.options,
              query = _a.query,
              variables = _a.variables,
              fetchPolicy_1 = _a.fetchPolicy;
          var hasClientExports = queryManager.transform(query).hasClientExports;

          var previousResult_1 = _this.updateLastResult(result);

          if (hasClientExports) {
            queryManager.getLocalState().addExportedVariables(query, variables).then(function (variables) {
              var previousVariables = _this.variables;

              if (!result.loading && previousResult_1 && fetchPolicy_1 !== 'cache-only' && !equal(previousVariables, variables)) {
                _this.setVariables(variables).then(function (updatedResult) {
                  iterateObserversSafely(_this.observers, 'next', updatedResult);
                });
              } else {
                _this.options.variables = variables;
                iterateObserversSafely(_this.observers, 'next', result);
              }
            });
          } else {
            iterateObserversSafely(_this.observers, 'next', result);
          }
        }
      },
      error: function error(_error) {
        _this.updateLastResult(__assign(__assign({}, _this.lastResult), {
          errors: _error.graphQLErrors,
          networkStatus: NetworkStatus.error,
          loading: false
        }));

        iterateObserversSafely(_this.observers, 'error', _this.lastError = _error);
      }
    };
    _this.isTornDown = false;
    _this.options = options;
    _this.queryId = queryManager.generateQueryId();
    _this.watching = shouldSubscribe;
    var opDef = getOperationDefinition(options.query);
    _this.queryName = opDef && opDef.name && opDef.name.value;
    _this.queryManager = queryManager;
    return _this;
  }

  Object.defineProperty(ObservableQuery.prototype, "variables", {
    get: function get() {
      return this.options.variables;
    },
    enumerable: true,
    configurable: true
  });

  ObservableQuery.prototype.result = function () {
    var _this = this;

    return new Promise(function (resolve, reject) {
      var observer = {
        next: function next(result) {
          resolve(result);

          _this.observers.delete(observer);

          if (!_this.observers.size) {
            _this.queryManager.removeQuery(_this.queryId);
          }

          setTimeout(function () {
            subscription.unsubscribe();
          }, 0);
        },
        error: reject
      };

      var subscription = _this.subscribe(observer);
    });
  };

  ObservableQuery.prototype.getCurrentResult = function () {
    var _a = this,
        lastResult = _a.lastResult,
        lastError = _a.lastError,
        fetchPolicy = _a.options.fetchPolicy;

    var isNetworkFetchPolicy = fetchPolicy === 'network-only' || fetchPolicy === 'no-cache';
    var networkStatus = lastError ? NetworkStatus.error : lastResult ? lastResult.networkStatus : isNetworkFetchPolicy ? NetworkStatus.loading : NetworkStatus.ready;
    var result = {
      data: !lastError && lastResult && lastResult.data || void 0,
      error: lastError,
      loading: isNetworkRequestInFlight(networkStatus),
      networkStatus: networkStatus
    };

    if (this.isTornDown) {
      return result;
    }

    var _b = this.queryManager.getCurrentQueryResult(this),
        data = _b.data,
        partial = _b.partial;

    Object.assign(result, {
      data: data,
      partial: partial
    });
    var queryStoreValue = this.queryManager.getQueryStoreValue(this.queryId);

    if (queryStoreValue) {
      var networkStatus_1 = queryStoreValue.networkStatus;

      if (hasError(queryStoreValue, this.options.errorPolicy)) {
        return Object.assign(result, {
          data: void 0,
          networkStatus: networkStatus_1,
          error: new ApolloError({
            graphQLErrors: queryStoreValue.graphQLErrors,
            networkError: queryStoreValue.networkError
          })
        });
      }

      if (queryStoreValue.variables) {
        this.options.variables = __assign(__assign({}, this.options.variables), queryStoreValue.variables);
      }

      Object.assign(result, {
        loading: isNetworkRequestInFlight(networkStatus_1),
        networkStatus: networkStatus_1
      });

      if (queryStoreValue.graphQLErrors && this.options.errorPolicy === 'all') {
        result.errors = queryStoreValue.graphQLErrors;
      }
    }

    if (!partial) {
      this.updateLastResult(result);
    }

    return result;
  };

  ObservableQuery.prototype.isDifferentFromLastResult = function (newResult) {
    return !equal(this.lastResultSnapshot, newResult);
  };

  ObservableQuery.prototype.getLastResult = function () {
    return this.lastResult;
  };

  ObservableQuery.prototype.getLastError = function () {
    return this.lastError;
  };

  ObservableQuery.prototype.resetLastResults = function () {
    delete this.lastResult;
    delete this.lastResultSnapshot;
    delete this.lastError;
    this.isTornDown = false;
  };

  ObservableQuery.prototype.resetQueryStoreErrors = function () {
    var queryStore = this.queryManager.getQueryStoreValue(this.queryId);

    if (queryStore) {
      queryStore.networkError = undefined;
      queryStore.graphQLErrors = [];
    }
  };

  ObservableQuery.prototype.refetch = function (variables) {
    var fetchPolicy = this.options.fetchPolicy;

    if (fetchPolicy === 'cache-only') {
      return Promise.reject(process.env.NODE_ENV === "production" ? new InvariantError(3) : new InvariantError('cache-only fetchPolicy option should not be used together with query refetch.'));
    }

    if (fetchPolicy !== 'no-cache' && fetchPolicy !== 'cache-and-network') {
      fetchPolicy = 'network-only';
    }

    if (variables && !equal(this.options.variables, variables)) {
      this.options.variables = __assign(__assign({}, this.options.variables), variables);
    }

    return this.queryManager.fetchQuery(this.queryId, __assign(__assign({}, this.options), {
      fetchPolicy: fetchPolicy
    }), FetchType.refetch);
  };

  ObservableQuery.prototype.fetchMore = function (fetchMoreOptions) {
    var _this = this;

    var combinedOptions = __assign(__assign({}, fetchMoreOptions.query ? fetchMoreOptions : __assign(__assign(__assign({}, this.options), fetchMoreOptions), {
      variables: __assign(__assign({}, this.options.variables), fetchMoreOptions.variables)
    })), {
      fetchPolicy: 'network-only'
    });

    var qid = this.queryManager.generateQueryId();
    return this.queryManager.fetchQuery(qid, combinedOptions, FetchType.normal, this.queryId).then(function (fetchMoreResult) {
      _this.updateQuery(function (previousResult) {
        var data = fetchMoreResult.data;
        var updateQuery = fetchMoreOptions.updateQuery;
        return updateQuery ? updateQuery(previousResult, {
          fetchMoreResult: data,
          variables: combinedOptions.variables
        }) : data;
      });

      _this.queryManager.stopQuery(qid);

      return fetchMoreResult;
    }, function (error) {
      _this.queryManager.stopQuery(qid);

      throw error;
    });
  };

  ObservableQuery.prototype.subscribeToMore = function (options) {
    var _this = this;

    var subscription = this.queryManager.startGraphQLSubscription({
      query: options.document,
      variables: options.variables
    }).subscribe({
      next: function next(subscriptionData) {
        var updateQuery = options.updateQuery;

        if (updateQuery) {
          _this.updateQuery(function (previous, _a) {
            var variables = _a.variables;
            return updateQuery(previous, {
              subscriptionData: subscriptionData,
              variables: variables
            });
          });
        }
      },
      error: function error(err) {
        if (options.onError) {
          options.onError(err);
          return;
        }

        process.env.NODE_ENV === "production" || invariant.error('Unhandled GraphQL subscription error', err);
      }
    });
    this.subscriptions.add(subscription);
    return function () {
      if (_this.subscriptions.delete(subscription)) {
        subscription.unsubscribe();
      }
    };
  };

  ObservableQuery.prototype.setOptions = function (opts) {
    var _a = this.options,
        oldFetchPolicy = _a.fetchPolicy,
        oldVariables = _a.variables;
    this.options = __assign(__assign({}, this.options), opts);

    if (opts.pollInterval) {
      this.startPolling(opts.pollInterval);
    } else if (opts.pollInterval === 0) {
      this.stopPolling();
    }

    var fetchPolicyChanged = oldFetchPolicy !== opts.fetchPolicy && (oldFetchPolicy === 'cache-only' || oldFetchPolicy === 'standby' || opts.fetchPolicy === 'network-only');
    return this.setVariables(this.options.variables, fetchPolicyChanged || !equal(this.variables, oldVariables));
  };

  ObservableQuery.prototype.setVariables = function (variables, tryFetch) {
    if (tryFetch === void 0) {
      tryFetch = !equal(this.variables, variables);
    }

    this.isTornDown = false;

    if (!tryFetch) {
      return this.observers.size ? this.result() : Promise.resolve();
    }

    this.options.variables = variables;

    if (!this.observers.size) {
      return Promise.resolve();
    }

    return this.queryManager.fetchQuery(this.queryId, this.options);
  };

  ObservableQuery.prototype.updateQuery = function (mapFn) {
    var queryManager = this.queryManager;

    var _a = queryManager.getQueryWithPreviousResult(this.queryId),
        previousResult = _a.previousResult,
        variables = _a.variables,
        document = _a.document;

    var newResult = tryFunctionOrLogError(function () {
      return mapFn(previousResult, {
        variables: variables
      });
    });

    if (newResult) {
      queryManager.cache.write({
        query: document,
        result: newResult,
        dataId: 'ROOT_QUERY',
        variables: variables
      });
      queryManager.broadcastQueries();
    }
  };

  ObservableQuery.prototype.stopPolling = function () {
    this.queryManager.stopPollingQuery(this.queryId);
    this.options.pollInterval = undefined;
  };

  ObservableQuery.prototype.startPolling = function (pollInterval) {
    this.options.pollInterval = pollInterval;
    this.queryManager.startPollingQuery(this.options, this.queryId);
  };

  ObservableQuery.prototype.updateLastResult = function (newResult) {
    var previousResult = this.lastResult;
    this.lastResult = newResult;
    this.lastResultSnapshot = this.queryManager.assumeImmutableResults ? newResult : cloneDeep(newResult);

    if (!isNonEmptyArray(newResult.errors)) {
      delete this.lastError;
    }

    return previousResult;
  };

  ObservableQuery.prototype.onSubscribe = function (observer) {
    var _this = this;

    if (observer === this.observer) {
      return function () {};
    }

    try {
      var subObserver = observer._subscription._observer;

      if (subObserver && !subObserver.error) {
        subObserver.error = defaultSubscriptionObserverErrorCallback;
      }
    } catch (_a) {}

    var first = !this.observers.size;
    this.observers.add(observer);
    if (observer.next && this.lastResult) observer.next(this.lastResult);
    if (observer.error && this.lastError) observer.error(this.lastError);

    if (first) {
      this.queryManager.observeQuery(this, this.observer).catch(this.observer.error);
    }

    return function () {
      if (_this.observers.delete(observer) && !_this.observers.size) {
        _this.tearDownQuery();
      }
    };
  };

  ObservableQuery.prototype.tearDownQuery = function () {
    var queryManager = this.queryManager;
    this.isTornDown = true;
    queryManager.stopPollingQuery(this.queryId);
    this.subscriptions.forEach(function (sub) {
      return sub.unsubscribe();
    });
    this.subscriptions.clear();
    queryManager.stopQuery(this.queryId);
    this.observers.clear();
  };

  return ObservableQuery;
}(Observable);

function defaultSubscriptionObserverErrorCallback(error) {
  process.env.NODE_ENV === "production" || invariant.error('Unhandled error', error.message, error.stack);
}

function iterateObserversSafely(observers, method, argument) {
  var observersWithMethod = [];
  observers.forEach(function (obs) {
    return obs[method] && observersWithMethod.push(obs);
  });
  observersWithMethod.forEach(function (obs) {
    return obs[method](argument);
  });
}

export { ObservableQuery, hasError };